import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import EmailForm from '../EmailForm';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import { emails as emailsApi } from '@afosto/messaging/api/Emails';
import { inboxes as inboxesApi } from '@afosto/messaging/api/Inboxes';
import translations from './translations';
import Autocomplete from '../Autocomplete';
import styled from 'styled-components';
import clsx from 'clsx';

const EmailFormWrapper = styled.div`
  position: relative;

  &::after {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    content: '';
    background-color: rgba(255, 255, 255, 0.5);
  }

  &.enabled::after {
    pointer-events: none;
    background-color: rgba(255, 255, 255, 0);
  }
`;

const EmailDrawer = ({ isOpen, onClose, onSubmit, inbox: providedInbox, email }) => {
  const intl = useIntl();
  const [contacts, setContacts] = useState([]);
  const [conceptEmail, setConceptEmail] = useState(email);
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [inboxes, setInboxes] = useState([]);
  const [inbox, setInbox] = useState(providedInbox);
  const confirm = useConfirm();

  const getRequestEmailData = emailData => {
    const { fqdn, prefix, label } = inbox;

    return {
      from: {
        name: label,
        email: `${prefix}@${fqdn}`,
      },
      to: emailData.to,
      cc: emailData.cc,
      bcc: emailData.bcc,
      subject: emailData.subject,
      html_body: emailData.content,
      attachments: emailData.attachments,
      folder: emailData.folder,
    };
  };

  const createEmail = async emailData => {
    try {
      setIsSavingEmail(true);
      const response = await emailsApi.createMail(getRequestEmailData(emailData));
      setIsSavingEmail(false);
      return response.data;
    } catch (err) {
      setIsSavingEmail(false);
      // Do something with error.
      return Promise.reject(err);
    }
  };

  const updateEmail = async (emailID, emailData) => {
    try {
      setIsSavingEmail(true);
      const response = await emailsApi.updateEmail(emailID, getRequestEmailData(emailData));
      setIsSavingEmail(false);
      return response.data;
    } catch (err) {
      // Do something with error.
      setIsSavingEmail(false);
      return Promise.reject(err);
    }
  };

  const mapToContacts = emails =>
    emails.reduce((acc, currentEmail) => {
      const contact = contacts.find(({ email: contactEmail }) => contactEmail === currentEmail);
      let name = currentEmail;

      if (contact) {
        name = `${contact.given_name} ${contact.additional_name} ${contact.family_name}`
          .replace(/\s\s+/g, ' ')
          .trim();
      }

      return [...acc, { name, email: currentEmail }];
    }, []);

  const formatEmailData = emailData => {
    const to = mapToContacts(emailData.to);
    const cc = mapToContacts(emailData.cc);
    const bcc = mapToContacts(emailData.bcc);

    return {
      ...emailData,
      to,
      cc,
      bcc,
    };
  };

  const handleCancel = async (formData, isValid) => {
    try {
      if (isValid && !isSavingEmail) {
        let changedEmail;

        if (conceptEmail && conceptEmail.id) {
          changedEmail = await updateEmail(conceptEmail.id, {
            ...formatEmailData({ ...formData, folder: 'concept' }),
          });
        } else {
          const confirmResponse = await confirm({
            title: intl.formatMessage(translations.confirmSaveAsConcept.title),
            description: intl.formatMessage(translations.confirmSaveAsConcept.description),
            buttons: [
              {
                label: intl.formatMessage(translations.confirmSaveAsConcept.buttons.cancel),
                type: 'cancel',
                variant: 'outlined',
                style: {
                  marginRight: 'auto',
                },
              },
              {
                label: intl.formatMessage(translations.confirmSaveAsConcept.buttons.discard),
                response: 'discard',
                type: 'submit',
                variant: 'outlined',
              },
              {
                label: intl.formatMessage(translations.confirmSaveAsConcept.buttons.save),
                response: 'save',
                type: 'submit',
                variant: 'contained',
                color: 'primary',
              },
            ],
          });

          if (confirmResponse === 'save') {
            changedEmail = await createEmail({
              ...formatEmailData({ ...formData, folder: 'concept' }),
            });
          }
        }

        if (onClose) {
          await onClose(changedEmail);
        }
      } else if (onClose) {
        await onClose();
      }
    } catch (err) {
      // Do something with error.
      if (onClose && err !== 'cancel') {
        await onClose();
      }
    }
  };

  const handleCloseDrawer = async form => {
    const isValid = await form.isValid();
    await handleCancel(form.getValues(), isValid);
  };

  const handleSubmit = async data => {
    if (onSubmit) {
      let changedEmail;

      if (conceptEmail && conceptEmail.id) {
        changedEmail = await updateEmail(conceptEmail.id, {
          ...formatEmailData(data),
          folder: 'sent',
        });
        setConceptEmail(changedEmail);
      } else {
        changedEmail = await createEmail({
          ...formatEmailData(data),
          folder: 'sent',
        });
        setConceptEmail(changedEmail);
      }

      onSubmit(changedEmail);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await contactsApi.listContacts({
        headers: {
          'x-page-size': 250,
        },
      });
      setContacts(response.data);
    } catch (err) {
      // Do something with error.
    }
  };

  useEffect(() => {
    setConceptEmail(email);
  }, [email, isOpen]);

  useEffect(() => {
    let isMounted = true;
    const getInboxes = async () => {
      try {
        const response = await inboxesApi.listInboxes();
        const { data = [] } = response;

        if (isMounted) {
          setInboxes(data);
        }
      } catch (error) {
        setInboxes([]);
      }
    };

    if (!providedInbox) {
      getInboxes();
    }

    return () => {
      isMounted = false;
    };
  }, [providedInbox]);

  useEffect(() => {
    if (isOpen) {
      fetchContacts();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen && !providedInbox) {
      setInbox(null);
    }
  }, [isOpen, providedInbox]);

  const renderInbox = item => (
    <ListItem dense>
      <ListItemText primary={item?.label} secondary={`${item?.prefix}@${item?.fqdn}`} />
    </ListItem>
  );

  return (
    <FormDrawer
      title={intl.formatMessage(translations.title)}
      isOpen={isOpen}
      onClose={handleCloseDrawer}
      drawerWidth="650px"
      skipConfirm
    >
      {!providedInbox && (
        <Autocomplete
          name="inbox"
          onChange={(ev, newValue) => setInbox(newValue)}
          label={intl.formatMessage(translations.fields.from)}
          options={inboxes}
          value={inbox}
          renderOption={renderInbox}
          autoFocus
        />
      )}
      <EmailFormWrapper className={clsx({ enabled: inbox })}>
        <EmailForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          email={conceptEmail}
          isSavingEmail={isSavingEmail}
          autoFocusFirstField={!!providedInbox}
        />
      </EmailFormWrapper>
    </FormDrawer>
  );
};

EmailDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  inbox: PropTypes.object,
  email: PropTypes.object,
};

EmailDrawer.defaultProps = {
  isOpen: false,
  onClose: undefined,
  onSubmit: undefined,
  inbox: null,
  email: null,
};

export default EmailDrawer;
